import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { isLoggedIn } from '@/auth/token';
import ThemeToggle from '@/theme/ThemeToggle';

type Props = {
  title: string;
  updated: string;
  testId: string;
  children: ReactNode;
};

export default function LegalLayout({ title, updated, testId, children }: Props) {
  const logged = isLoggedIn();

  useEffect(() => {
    document.title = `${title} · Sourcetape`;
    window.scrollTo(0, 0);
    return () => {
      document.title = 'Sourcetape — Launchpad for founders';
    };
  }, [title]);

  return (
    <div className="min-h-screen bg-bg text-textp overflow-y-auto" data-testid={testId}>
      <header className="sticky top-0 z-20 border-b border-border/80 bg-bg/85 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <span className="w-7 h-7 rounded-md bg-accentSoft border border-accentLine flex items-center justify-center">
              <i className="ri-stack-line text-accent" />
            </span>
            Sourcetape
          </Link>
          <div className="flex items-center gap-2">
            {logged ? (
              <Link to="/dashboard" className="text-sm text-texts hover:text-textp px-2 py-1.5">Dashboard</Link>
            ) : (
              <Link to="/login" className="text-sm text-texts hover:text-textp px-2 py-1.5">Sign in</Link>
            )}
            <ThemeToggle className="px-2 py-1.5" />
            <Link to="/" className="text-sm font-medium bg-accent hover:bg-accent-hover text-accent-text px-3 py-1.5 rounded-md">
              Get started
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 pt-16 pb-24">
        <p className="text-xs uppercase tracking-[0.2em] text-accent mb-4">Legal</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-3 text-sm text-texts">Last updated {updated}</p>
        <div className="mt-10 space-y-8 text-[15px] text-texts leading-relaxed">{children}</div>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-5xl mx-auto px-5 py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-texts">
          <span>Sourcetape · sourcetape.io</span>
          <div className="flex flex-wrap gap-4">
            <Link to="/" className="hover:text-textp">Home</Link>
            <Link to="/studio" className="hover:text-textp">Studio</Link>
            <Link to="/privacy" className="hover:text-textp">Privacy</Link>
            <Link to="/terms-of-use" className="hover:text-textp">Terms of Use</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-medium text-textp tracking-tight">{title}</h2>
      {children}
    </section>
  );
}
